<script lang="ts">
  import Swal from 'sweetalert2';
  import {STEP} from '@/types/common';
  import {step, project, subtitles} from '@/store';
  import {triggerOnSeek} from '@components';
  import disabled from '@/store/disabled';

  import {Container, VideoPlayer, SubtitleSidebar} from '@components';

  import HooksSidebar from '@/components/subtitle-sidebar/HooksSidebar.svelte';
  import {topnav} from '@/utils/top-nav-next-custom-events';
  import {onDestroy, onMount} from 'svelte';
  import Dialog from '@/components/dialog/Dialog.svelte';

  const two_minutes = 156;
  let play: boolean = false;
  let currentTime: number = 0;
  let doNotScroll: boolean = false;
  let selectedHook: string;
  let selectedHookIndex: number;
  let isOpen: boolean = true;
  $: $disabled = $project.selectedSentences.length === 0 || $project.totalDuration > two_minutes;

  let isLoading = false;

  const updateNextStep = async () => {
    if ($disabled) return openSwal();
    if ($project.changes.length) await saveChanges();
    step.update(STEP.PREVIEW);
  };

  const updatePrevStep = async () => {
    if ($project.changes.length) {
      await saveChanges();
    }

    step.update(STEP.CHOOSE_TEMPLATE);
  };

  const openSwal = () => {
    let html;
    let title;

    if ($project.totalDuration > 156) {
      html = `To maintain engagement and comply with most platform limits, we advise keeping clips under 2 minutes and 30 seconds. Shorter videos tend to retain viewer attention better.`;
      title = 'Warning: Maximum Clip Length.';
      /* 
            html = `Video should only last 5 minutes. <br />
            Current video duration: <span class="font-medium"> ${toTime(
            $project.totalDuration,
            )}</span>`;
            */
    } else {
      html = 'Please select atleast one (1) subtitle to continue.';
      title = 'Warning: No Captions.';
    }

    Swal.fire({
      html,
      icon: 'warning',
      titleText: title
    });
  };

  const saveChanges = async () => {
    isLoading = true;
    await project.updateTranscription().then(async () => {
      await subtitles.getSubtitles($project.video?.id as number);
    });
    isLoading = false;
    project.clearChanges();
    project.set({
      compiledVideo: null,
      compiledAICameraCrewVideo: null,
      usesAICameraCrew: false,
      usesAICameraCrewV2: false,
      compiledAICameraCrewVideoV2: null,
      isNew: true,
      isUpdated: false
    });
  };

  const getNewCurrentTime = (e: CustomEvent) => {
    currentTime = e.detail.timestamp;
    play = e.detail.play;
  };

  const getSelectedTimestamp = (e: CustomEvent) => {
    currentTime = e.detail.selectedTimestamp;
    doNotScroll = true;
  };

  const onSeek = (e: CustomEvent) => {
    triggerOnSeek(e.detail.time);
  };

  const setSelections = (event: CustomEvent) => {
    selectedHook = event.detail.hook;
    selectedHookIndex = event.detail.hookIndex;
  };

  $: topnav.dispatchEvent({
    nextDisabled: $disabled,
    nextDisabledTip:
      $project.totalDuration > two_minutes
        ? 'You must keep clips under 2 minutes and 30 seconds'
        : $disabled
          ? 'You must select a subtitle to proceed'
          : ''
  });

  onMount(() => {
    topnav.disabledNext(openSwal);
    topnav.next(updateNextStep);
    topnav.previous(updatePrevStep);
  });
</script>

<div id="select_subtitles" />
<Container className="flex">
  {#if $project.video?.transcription.transcriptionId}
    <HooksSidebar video={$project.video} bind:isOpen on:set-selections={setSelections} />
  {/if}
  <SubtitleSidebar
    bind:currentTime
    bind:doNotScroll
    bind:selectedHook
    bind:selectedHookIndex
    on:on-sentence-scroll={getNewCurrentTime}
    on:select-sentence={getSelectedTimestamp}
  />
  <div class="relative flex h-full flex-grow items-center overflow-auto">
    <VideoPlayer bind:play on:video-seek={onSeek} />
  </div>
</Container>
<Dialog isDialogOpen={isLoading}>
  <div class="pointer-events-none flex h-full w-full items-center justify-center">
    <div
      class="pointer-events-auto mx-4 h-auto w-full min-w-[280px] max-w-[400px] rounded-2xl border border-gray-500 bg-white shadow-lg dark:border-zinc-600 dark:bg-zinc-700"
    >
      <div class="flex flex-col justify-center p-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2 py-1.5">
            <h1 class="text-xl font-bold">Updating Transcription</h1>
          </div>
        </div>
        <hr class="border-t border-t-gray-300 dark:border-zinc-600" />
        <div class="grid w-full gap-1 pt-4">
          <div class="relative h-[1rem] w-full overflow-hidden rounded-md bg-zinc-300">
            <div class="loader-color-primary bg-rose-500" style="--delay: 3s;" />
            <div class="loader-color-secondary bg-zinc-300" style="--delay: 3s;" />
          </div>
        </div>
      </div>
    </div>
  </div>
</Dialog>

<style>
  .loader-color-primary {
    animation: loaderprimary var(--delay, 3s) infinite;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
  .loader-color-secondary {
    animation: loadersecondary var(--delay, 3s) infinite;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }

  @keyframes loaderprimary {
    0% {
      width: 0%;
    }
    70% {
      width: 100%;
    }
    100% {
      width: 100%;
    }
  }

  @keyframes loadersecondary {
    0% {
      width: 0%;
    }
    10% {
      width: 0%;
    }
    100% {
      width: 100%;
    }
  }
</style>
